import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { ErrorModal } from '../../../components/ui/ErrorModal';

const TruckAssignmentModal = ({ order, onClose, onDone }) => {
  const [assignments, setAssignments] = useState([]);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingRowKey, setSavingRowKey] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [totalTrucksInSystem, setTotalTrucksInSystem] = useState(0);
  const [assignedTrucksCount, setAssignedTrucksCount] = useState(0);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  // Blank row template
  const blankRow = () => ({
    _key: Date.now() + Math.random(),
    vehicle_number: '',
    truck_listing_id: null,
    driver_name: '',
    driver_phone: '',
    capacity_tons: '',
    assigned_quantity: '',
    rate_per_ton: '',
    destination: order?.deliveryDestination || '',
    offer_id: order?.readableBookingId || order?.readableOfferId || order?.poNumber || '',
    notes: '',
    dl_number: '',
    _saved: false,
  });

  // Capacity-based sequential fill: assign min(truck capacity, remaining quantity) per truck
  const calculateCapacityBasedDistribution = async (trucks, totalOrderQuantity) => {
    let remaining = totalOrderQuantity;
    const result = [];

    for (const truck of trucks) {
      if (remaining <= 0) {
        result?.push({ ...truck });
        continue;
      }

      // Fetch actual capacity from trucks table
      let truckCapacity = parseFloat(truck?.capacity_tons) || 0;
      if (truck?.vehicle_number) {
        const { data: truckData } = await supabase
          ?.from('trucks')
          ?.select('capacity_mt')
          ?.eq('vehicle_number', truck?.vehicle_number)
          ?.single();
        if (truckData?.capacity_mt) {
          truckCapacity = parseFloat(truckData?.capacity_mt);
        }
      }

      // Fallback to 30 if capacity still unknown
      if (truckCapacity <= 0) truckCapacity = 30;

      const assignedQty = Math.min(truckCapacity, remaining);
      remaining -= assignedQty;

      result?.push({
        ...truck,
        capacity_tons: String(truckCapacity),
        assigned_quantity: String(Math.round(assignedQty * 100) / 100),
      });
    }

    return result;
  };

  // Fetch available vehicles from Supabase
  const fetchAvailableVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    const { data, error } = await supabase
      ?.from('trucks')
      ?.select('id, vehicle_number, capacity_mt, rate_per_ton, current_location')
      ?.eq('status', 'Available')
      ?.order('vehicle_number', { ascending: true });
    if (!error && data) {
      setAvailableVehicles(data);
    }
    setLoadingVehicles(false);
  }, []);

  // Fetch total trucks in system and currently assigned trucks count for dynamic "Available vehicles"
  const fetchAvailableVehiclesCount = useCallback(async () => {
    try {
      // Total verified/registered trucks in system
      const { count: totalCount } = await supabase
        ?.from('trucks')
        ?.select('id', { count: 'exact', head: true });

      // Currently assigned trucks (distinct vehicle_numbers in order_truck_assignments with status 'assigned')
      const { data: assignedData } = await supabase
        ?.from('order_truck_assignments')
        ?.select('vehicle_number')
        ?.eq('status', 'assigned');

      const uniqueAssigned = new Set(assignedData?.map(r => r?.vehicle_number) || []);

      setTotalTrucksInSystem(totalCount || 0);
      setAssignedTrucksCount(uniqueAssigned?.size || 0);
    } catch (err) {
      console.error('Error fetching vehicle count:', err);
    }
  }, []);

  // Load existing assignments for this booking
  const loadExisting = useCallback(async () => {
    if (!order?.id) return;
    const { data, error } = await supabase
      ?.from('order_truck_assignments')
      ?.select('*')
      ?.eq('booking_id', order?.id)
      ?.order('assigned_at', { ascending: true });

    if (!error && data?.length > 0) {
      const rows = data?.map(r => ({ ...r, _key: r?.id, _saved: true }));
      // Check if quantities look like an equal split (all equal fractional values)
      // If so, redistribute by capacity when modal opens
      const quantities = rows?.map(r => Number(r?.assigned_quantity || 0));
      const allEqual = quantities?.length > 1 && quantities?.every(q => Math.abs(q - quantities?.[0]) < 0.01);
      const hasFractional = quantities?.some(q => q % 1 !== 0);
      if (allEqual && hasFractional) {
        // Redistribute by capacity — reset assigned_quantity to 0 so distribution runs
        const resetRows = rows?.map(r => ({ ...r, assigned_quantity: '' }));
        setAssignments(resetRows);
      } else {
        setAssignments(rows);
      }
      setTotalAssigned(quantities?.reduce((s, q) => s + q, 0));
    } else {
      // Start with one blank row; capacity-based distribution applied after vehicle selection
      setAssignments([blankRow()]);
      setTotalAssigned(0);
    }
  }, [order?.id]);

  useEffect(() => {
    loadExisting();
    fetchAvailableVehicles();
    fetchAvailableVehiclesCount();
  }, [loadExisting, fetchAvailableVehicles, fetchAvailableVehiclesCount]);

  // Apply capacity-based sequential distribution whenever assignments or orderQty change
  // and there are unassigned rows with vehicles selected
  useEffect(() => {
    const orderQuantity = Number(order?.qtyRequired || 0);
    if (assignments?.length === 0 || orderQuantity <= 0) return;

    // Always run capacity-based distribution (removed hasUnassignedWithVehicle check)

    let cancelled = false;
    calculateCapacityBasedDistribution(assignments, orderQuantity)?.then(distributed => {
      if (cancelled) return;
      // Only update if distribution actually changed something
      const changed = distributed?.some((d, i) => d?.assigned_quantity !== assignments?.[i]?.assigned_quantity);
      if (changed) {
        setAssignments(distributed);
      }
    });
    return () => { cancelled = true; };
  }, [assignments?.length, order?.qtyRequired, assignments?.map(r => r?.vehicle_number)?.join(',')]);

  // Subscribe to order_truck_assignments changes to update available vehicles count live
  useEffect(() => {
    const channel = supabase
      ?.channel('truck_assignment_modal_count')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'order_truck_assignments' }, () => {
        fetchAvailableVehiclesCount();
        fetchAvailableVehicles();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [fetchAvailableVehiclesCount, fetchAvailableVehicles]);

  // Recalculate total whenever assignments change
  useEffect(() => {
    const total = assignments?.reduce((s, r) => s + Number(r?.assigned_quantity || 0), 0);
    setTotalAssigned(total);
  }, [assignments]);

  const showNotif = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  const addRow = () => setAssignments(prev => [...prev, blankRow()]);

  const removeRow = (key) => {
    if (assignments?.length === 1) return;
    setAssignments(prev => prev?.filter(r => r?._key !== key));
  };

  // Get vehicle numbers already selected in OTHER rows (to prevent duplicates)
  const getUsedVehicleNumbers = (currentKey) => {
    return assignments
      ?.filter(r => r?._key !== currentKey && r?.vehicle_number)
      ?.map(r => r?.vehicle_number);
  };

  // Get available vehicles for a specific row (exclude already-used ones)
  const getVehiclesForRow = (rowKey) => {
    const used = getUsedVehicleNumbers(rowKey);
    return availableVehicles?.filter(v => !used?.includes(v?.vehicle_number));
  };

  const updateRow = (key, field, value) => {
    setAssignments(prev =>
      prev?.map(r => {
        if (r?._key !== key) return r;
        const updated = { ...r, [field]: value, _saved: false };

        // ISSUE 3 FIX: When vehicle is selected, fetch actual capacity from trucks table
        if (field === 'vehicle_number' && value) {
          const vehicle = availableVehicles?.find(v => v?.vehicle_number === value);
          if (vehicle) {
            updated.capacity_tons = String(vehicle?.capacity_mt || '');
            if (!r?.rate_per_ton) {
              updated.rate_per_ton = String(vehicle?.rate_per_ton || '');
            }
          } else {
            supabase
              ?.from('trucks')
              ?.select('capacity_mt, rate_per_ton')
              ?.eq('vehicle_number', value)
              ?.single()
              ?.then(({ data: truckData }) => {
                if (truckData) {
                  setAssignments(prev2 =>
                    prev2?.map(r2 =>
                      r2?._key === key
                        ? {
                            ...r2,
                            capacity_tons: String(truckData?.capacity_mt || ''),
                            rate_per_ton: r2?.rate_per_ton || String(truckData?.rate_per_ton || ''),
                          }
                        : r2
                    )
                  );
                }
              });
          }
          // Fetch latest available truck_listing_id for transporter attribution
          supabase
            ?.from('truck_listings')
            ?.select('id')
            ?.eq('vehicle_number', value)
            ?.eq('status', 'Available')
            ?.order('created_at', { ascending: false })
            ?.limit(1)
            ?.maybeSingle()
            ?.then(({ data: listingData }) => {
              if (listingData?.id) {
                setAssignments(prev2 =>
                  prev2?.map(r2 =>
                    r2?._key === key
                      ? { ...r2, truck_listing_id: listingData.id }
                      : r2
                  )
                );
              }
            });
        }

        // Capacity guard: assigned_quantity cannot exceed capacity_tons
        if (field === 'assigned_quantity') {
          const cap = Number(updated?.capacity_tons) || Infinity;
          if (Number(value) > cap) {
            updated[field] = String(cap);
          }
        }
        return updated;
      })
    );
  };

  let remaining = Number(order?.qtyRequired || 0) - totalAssigned;

  const validateRow = (r) => {
    if (!r?.vehicle_number?.trim()) return 'Vehicle number is required.';
    if (!r?.driver_name?.trim()) return 'Driver name is required.';
    if (!r?.driver_phone?.trim()) return 'Driver phone is required.';
    if (!Number(r?.assigned_quantity) || Number(r?.assigned_quantity) <= 0)
      return 'Assigned quantity must be > 0.';
    if (!Number(r?.rate_per_ton) || Number(r?.rate_per_ton) <= 0)
      return 'Rate per ton must be > 0.';
    if (Number(r?.capacity_tons) > 0 && Number(r?.assigned_quantity) > Number(r?.capacity_tons))
      return `Assigned quantity (${r?.assigned_quantity} MT) exceeds truck capacity (${r?.capacity_tons} MT).`;
    return null;
  };

  // Save a single truck row
  const handleSaveTruck = async (rowKey) => {
    const row = assignments?.find(r => r?._key === rowKey);
    if (!row) return;

    const err = validateRow(row);
    if (err) {
      setErrorModal({ show: true, message: err });
      return;
    }

    // ISSUE 4 FIX: Fetch actual capacity from trucks table before validation
    let actualCapacity = parseFloat(row?.capacity_tons) || 0;
    if (row?.vehicle_number) {
      const { data: truckData } = await supabase
        ?.from('trucks')
        ?.select('capacity_mt')
        ?.eq('vehicle_number', row?.vehicle_number?.trim()?.toUpperCase())
        ?.single();
      if (truckData?.capacity_mt) {
        actualCapacity = parseFloat(truckData?.capacity_mt);
        // Also update the row's capacity_tons to reflect the real value
        setAssignments(prev =>
          prev?.map(r => r?._key === rowKey ? { ...r, capacity_tons: String(actualCapacity) } : r)
        );
      }
    }

    // Per-truck capacity validation with actual DB capacity
    const assignedQty = parseFloat(row?.assigned_quantity);
    if (actualCapacity > 0 && assignedQty > actualCapacity) {
      setErrorModal({
        show: true,
        message: `Cannot assign ${assignedQty} MT to truck ${row?.vehicle_number}. This truck has an RTA-certified capacity of ${actualCapacity} MT. Assigning more would violate transport regulations.`,
      });
      return;
    }

    // Check duplicate vehicle in same order
    const duplicates = assignments?.filter(r => r?._key !== rowKey && r?.vehicle_number === row?.vehicle_number && r?._saved);
    if (duplicates?.length > 0) {
      setErrorModal({ show: true, message: `Vehicle ${row?.vehicle_number} is already assigned in this order.` });
      return;
    }

    setSavingRowKey(rowKey);
    try {
      const rowData = {
        booking_id: order?.id,
        vehicle_number: row?.vehicle_number?.trim()?.toUpperCase(),
        driver_name: row?.driver_name?.trim() || '',
        driver_phone: row?.driver_phone?.trim() || '',
        capacity_tons: actualCapacity || Number(row?.capacity_tons) || 0,
        assigned_quantity: Number(row?.assigned_quantity),
        rate_per_ton: Number(row?.rate_per_ton),
        destination: row?.destination?.trim() || order?.deliveryDestination || '',
        offer_id: row?.offer_id?.trim() || '',
        notes: row?.notes?.trim() || '',
        assigned_by: 'quota_manager',
        status: 'assigned',
        readable_booking_id: order?.readableBookingId || order?.readableOfferId || '',
        truck_listing_id: row?.truck_listing_id || null,
      };

      let savedId = row?.id;

      if (row?.id && typeof row?.id === 'string' && row?.id?.length > 10) {
        // Update existing DB row
        const { error: updateErr } = await supabase
          ?.from('order_truck_assignments')
          ?.update(rowData)
          ?.eq('id', row?.id);
        if (updateErr) throw updateErr;
      } else {
        // Insert new row
        const { data: inserted, error: insertErr } = await supabase
          ?.from('order_truck_assignments')
          ?.insert([rowData])
          ?.select('id')
          ?.single();
        if (insertErr) throw insertErr;
        savedId = inserted?.id;
      }

      // Mark row as saved and update its id
      setAssignments(prev =>
        prev?.map(r => r?._key === rowKey ? { ...r, id: savedId, _saved: true } : r)
      );

      // Update buyer_bookings summary (first vehicle + first rate for backward compat)
      const allSaved = assignments?.map(r => r?._key === rowKey ? { ...r, _saved: true } : r);
      const firstSaved = allSaved?.find(r => r?._saved);
      if (firstSaved) {
        await supabase
          ?.from('buyer_bookings')
          ?.update({
            assigned_vehicle_number: firstSaved?.vehicle_number,
            transporter_rate_per_ton: Number(firstSaved?.rate_per_ton),
            driver_mobile: firstSaved?.driver_phone?.trim() || null,
            updated_at: new Date()?.toISOString(),            
          })
          ?.eq('id', order?.id);
      }

      // Update trucks.status to 'Assigned' for the saved vehicle (status only, never capacity)
      if (rowData?.vehicle_number) {
        const { error: statusError } = await supabase
          ?.from('trucks')
          ?.update({ status: 'Assigned' })
          ?.eq('vehicle_number', rowData?.vehicle_number);
        if (statusError) {
          console.error('Truck status update failed:', statusError);
        }
      }

      // Update truck_listings.status to 'Assigned' for transporter attribution
      if (row?.truck_listing_id) {
        await supabase
          ?.from('truck_listings')
          ?.update({ status: 'Assigned' })
          ?.eq('id', row?.truck_listing_id);
      }

      showNotif(`Truck ${row?.vehicle_number} saved successfully!`, 'success');
      // Fire TrustHathi certificate — non-blocking
supabase.functions.invoke('truck-assignment-notify', {
  body: {
    booking_id: order?.id,
    vehicle_number: row?.vehicle_number?.trim()?.toUpperCase(),
    driver_name: row?.driver_name?.trim() || null,
    driver_phone: row?.driver_phone?.trim() || null,
    dl_number: row?.dl_number?.trim() || null,
    assigned_quantity: Number(row?.assigned_quantity),
    truck_assignment_id: savedId,
  },
}).then(({ error }) => {
  if (error) console.error('TrustHathi notify failed:', error);
}).catch(err => console.error('TrustHathi notify error:', err));

      // Refresh available vehicles so saved vehicle is removed from remaining dropdowns
      await fetchAvailableVehicles();
      await fetchAvailableVehiclesCount();
    } catch (e) {
      console.error('Save truck row error:', e);
      showNotif('Failed to save truck. Please try again.', 'error');
    }
    setSavingRowKey(null);
  };

  const validate = () => {
    for (const r of assignments) {
      const err = validateRow(r);
      if (err) return err;
    }
    // Validate per-truck capacity
    for (const r of assignments) {
      const assignedQty = parseFloat(r?.assigned_quantity || 0);
      const capacity = parseFloat(r?.capacity_tons || 0);
      if (capacity > 0 && assignedQty > capacity) {
        return `Truck ${r?.vehicle_number}: Cannot assign ${assignedQty} MT (RTA capacity: ${capacity} MT). Please reduce the assigned quantity.`;
      }
    }
    // Validate total MT does not exceed order quantity (partial assignment allowed — no truck count check)
    // Round to 2 decimal places to avoid floating-point false positives (e.g. 200.000000000001)
    const totalMT = Math.round(assignments?.reduce((s, r) => s + Number(r?.assigned_quantity || 0), 0) * 100) / 100;
    const orderQtyRounded = Math.round(Number(order?.qtyRequired) * 100) / 100;
    if (totalMT > orderQtyRounded) {
      return `Total assigned (${totalMT} MT) exceeds order quantity (${order?.qtyRequired} MT). Please adjust assignments.`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setErrorModal({ show: true, message: err });
      return;
    }

    setSaving(true);
    try {
      // Fetch previously assigned vehicle numbers BEFORE deleting (to revert removed trucks)
      const { data: prevAssignments } = await supabase
        ?.from('order_truck_assignments')
        ?.select('vehicle_number, truck_listing_id')
        ?.eq('booking_id', order?.id);
      const prevVehicleNumbers = (prevAssignments || [])
        ?.map(r => r?.vehicle_number?.trim()?.toUpperCase())
        ?.filter(v => v && v !== '');

      // Delete existing assignments for this booking (replace strategy)
      await supabase
        ?.from('order_truck_assignments')
        ?.delete()
        ?.eq('booking_id', order?.id);

      // Insert all rows
      const rows = assignments?.map(r => ({
        booking_id: order?.id,
        vehicle_number: r?.vehicle_number?.trim()?.toUpperCase(),
        driver_name: r?.driver_name?.trim() || '',
        driver_phone: r?.driver_phone?.trim() || '',
        capacity_tons: Number(r?.capacity_tons) || 0,
        assigned_quantity: Number(r?.assigned_quantity),
        rate_per_ton: Number(r?.rate_per_ton),
        destination: r?.destination?.trim() || order?.deliveryDestination || '',
        offer_id: r?.offer_id?.trim() || '',
        notes: r?.notes?.trim() || '',
        assigned_by: 'quota_manager',
        status: 'assigned',
        readable_booking_id: order?.readableBookingId || order?.readableOfferId || '',
        truck_listing_id: r?.truck_listing_id || null,
      }));

      const { error: insertErr } = await supabase
        ?.from('order_truck_assignments')
        ?.insert(rows);

      if (insertErr) throw insertErr;

      // Update buyer_bookings with summary (first vehicle + first rate for backward compat)
      const firstRow = rows?.[0];
      await supabase
        ?.from('buyer_bookings')
        ?.update({
          assigned_vehicle_number: firstRow?.vehicle_number,
          transporter_rate_per_ton: firstRow?.rate_per_ton,
          driver_mobile: firstRow?.driver_phone?.trim() || null,
          updated_at: new Date()?.toISOString(),          
        })
        ?.eq('id', order?.id);

      showNotif(`${rows?.length} truck(s) assigned successfully!`, 'success');

      // Build vehicle number array from the rows payload (already trimmed/uppercased)
      // This ensures exact match with trucks table vehicle_number values
      const assignedVehicles = rows
        ?.map(r => r?.vehicle_number)
        ?.filter(v => v && v !== '');

      console.log('Updating truck status for:', assignedVehicles);

      // Update newly assigned trucks to 'Assigned'
      if (assignedVehicles?.length > 0) {
        const { error: statusError } = await supabase
          ?.from('trucks')
          ?.update({ status: 'Assigned' })
          ?.in('vehicle_number', assignedVehicles);
        if (statusError) {
          console.error('Truck status update failed:', statusError);
        }
      }

      // Revert trucks that were previously assigned but are no longer in the new list back to 'Available'
      const removedVehicles = prevVehicleNumbers?.filter(v => !assignedVehicles?.includes(v));
      if (removedVehicles?.length > 0) {
        console.log('Reverting removed trucks to Available:', removedVehicles);
        const { error: revertError } = await supabase
          ?.from('trucks')
          ?.update({ status: 'Available' })
          ?.in('vehicle_number', removedVehicles);
        if (revertError) {
          console.error('Truck status revert failed:', revertError);
        }
      }

      // Update truck_listings.status to 'Assigned' for newly assigned listings
      const assignedListingIds = rows
        ?.map(r => r?.truck_listing_id)
        ?.filter(id => id);
      if (assignedListingIds?.length > 0) {
        await supabase
          ?.from('truck_listings')
          ?.update({ status: 'Assigned' })
          ?.in('id', assignedListingIds);
      }

      // Revert removed truck listings back to 'Available'
      const removedListingIds = (prevAssignments || [])
        ?.filter(r => removedVehicles?.includes(r?.vehicle_number?.trim()?.toUpperCase()))
        ?.map(r => r?.truck_listing_id)
        ?.filter(id => id);
      if (removedListingIds?.length > 0) {
        await supabase
          ?.from('truck_listings')
          ?.update({ status: 'Available' })
          ?.in('id', removedListingIds);
      }

      setTimeout(() => { onDone?.(); onClose?.(); }, 1500);
    } catch (e) {
      console.error('TruckAssignmentModal save error:', e);
      showNotif('Failed to save assignments. Please try again.', 'error');
    }
    setSaving(false);
  };

  const orderQty = Number(order?.qtyRequired || 0);
  const pctAssigned = orderQty > 0 ? Math.min(100, (totalAssigned / orderQty) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#fef3c7' }}>
              <Icon name="Truck" size={20} color="#d97706" />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: '#1f2937' }}>Assign Trucks</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs" style={{ color: '#9ca3af' }}>Booking Ref:</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(217,119,6,0.1)', color: '#b45309', border: '1px solid rgba(217,119,6,0.25)' }}>
                  # {order?.readableBookingId || order?.readableOfferId || order?.poNumber}
                </span>
              </div>
              <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                {order?.factoryName} · {order?.sugarGrade} · {orderQty?.toLocaleString('en-IN')} MT total
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-amber-100 transition-colors">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-4 border-b" style={{ borderColor: '#f3f4f6' }}>
          <div className="flex items-center justify-between text-xs mb-2">
            <span style={{ color: '#374151' }}>
              <span className="font-semibold">{totalAssigned?.toLocaleString('en-IN')} MT</span> assigned
            </span>
            <span
              className="font-semibold"
              style={{ color: remaining < 0 ? '#dc2626' : remaining === 0 ? '#059669' : '#d97706' }}
            >
              {remaining < 0
                ? `${Math.abs(remaining)?.toLocaleString('en-IN')} MT over-assigned!`
                : remaining === 0
                ? '✓ Fully assigned'
                : `${remaining?.toLocaleString('en-IN')} MT remaining`}
            </span>
          </div>
          <div className="w-full h-2.5 rounded-full" style={{ background: '#e5e7eb' }}>
            <div
              className="h-2.5 rounded-full transition-all duration-300"
              style={{
                width: `${pctAssigned}%`,
                background: remaining < 0 ? '#dc2626' : pctAssigned === 100 ? '#059669' : '#f97316',
              }}
            />
          </div>
          <p className="text-xs mt-1.5" style={{ color: '#9ca3af' }}>
            Order: {orderQty?.toLocaleString('en-IN')} MT · Trucks: {assignments?.length} · Available vehicles: {Math.max(0, totalTrucksInSystem - assignedTrucksCount)}
          </p>
          {remaining > 0 && (
            <p className="text-xs mt-1" style={{ color: '#d97706' }}>
              Partial assignment allowed — you can save now and assign remaining {remaining?.toLocaleString('en-IN')} MT later.
            </p>
          )}
        </div>

        {/* Notification */}
        {notification?.show && (
          <div
            className="mx-6 mt-4 flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-white"
            style={{ background: notification?.type === 'success' ? '#059669' : '#dc2626' }}
          >
            <Icon name={notification?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} color="#fff" />
            {notification?.message}
          </div>
        )}

        {/* Truck rows */}
        <div className="px-6 py-4 space-y-4 max-h-[55vh] overflow-y-auto">
          {assignments?.map((row, idx) => (
            <div
              key={row?._key}
              className="rounded-xl p-4 space-y-3"
              style={{
                background: row?._saved ? '#f0fdf4' : '#fdf8f0',
                border: `1px solid ${row?._saved ? '#bbf7d0' : '#e8ddd0'}`,
              }}
            >
              {/* Row header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                    Truck #{idx + 1}
                  </span>
                  {row?._saved && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: '#dcfce7', color: '#166534' }}>
                      <Icon name="CheckCircle" size={11} color="#166534" /> Saved
                    </span>
                  )}
                </div>
                {assignments?.length > 1 && (
                  <button
                    onClick={() => removeRow(row?._key)}
                    className="p-1 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Icon name="Trash2" size={14} color="#ef4444" />
                  </button>
                )}
              </div>

              {/* Row fields — 2 column grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Vehicle Number — Dropdown */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>
                    Vehicle Number <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  {loadingVehicles ? (
                    <div className="w-full px-3 py-2 rounded-lg text-sm border flex items-center gap-2" style={{ border: '1px solid #d1d5db', background: '#f9fafb' }}>
                      <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                      <span style={{ color: '#9ca3af' }}>Loading vehicles…</span>
                    </div>
                  ) : (
                    <select
                      value={row?.vehicle_number}
                      onChange={e => updateRow(row?._key, 'vehicle_number', e?.target?.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                      style={{ border: '1px solid #d1d5db', color: row?.vehicle_number ? '#1f2937' : '#9ca3af', background: '#fff' }}
                    >
                      <option value="">— Select Vehicle —</option>
                      {row?.vehicle_number && !getVehiclesForRow(row?._key)?.find(v => v?.vehicle_number === row?.vehicle_number) && (
                        <option value={row?.vehicle_number}>{row?.vehicle_number} (currently assigned)</option>
                      )}
                      {getVehiclesForRow(row?._key)?.map(v => (
                        <option key={v?.id} value={v?.vehicle_number}>
                          {v?.vehicle_number} — {v?.capacity_mt} MT{v?.current_location ? ` · ${v?.current_location}` : ''}
                        </option>
                      ))}
                      {getVehiclesForRow(row?._key)?.length === 0 && !row?.vehicle_number && (
                        <option disabled value="">No available vehicles</option>
                      )}
                    </select>
                  )}
                  {row?.vehicle_number && (() => {
                    const v = availableVehicles?.find(veh => veh?.vehicle_number === row?.vehicle_number);
                    return v ? (
                      <p className="text-xs" style={{ color: '#6b7280' }}>
                        Capacity: {v?.capacity_mt} MT · Rate: ₹{v?.rate_per_ton}/ton
                      </p>
                    ) : null;
                  })()}
                </div>

                {/* Offer ID */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>
                    Booking Ref / Transporter Ref
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. IDON-SKSAPL-260421-25"
                    value={row?.offer_id}
                    onChange={e => updateRow(row?._key, 'offer_id', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                </div>

                {/* Truck Capacity — READ ONLY (RTA-certified) */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>
                    Truck Capacity (MT)
                  </label>
                  <input
                    type="number"
                    value={row?.capacity_tons}
                    disabled
                    className="w-full px-3 py-2 rounded-lg text-sm border bg-gray-100 cursor-not-allowed opacity-75"
                    style={{ border: '1px solid #d1d5db', color: '#6b7280' }}
                    title="Truck capacity is RTA-certified and cannot be modified"
                  />
                  <p className="text-xs text-gray-500 mt-1">RTA-certified capacity (read-only)</p>
                </div>

                {/* Assigned Quantity */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>
                    Assigned Quantity (MT) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 30"
                    min="0"
                    max={
                      Number(row?.capacity_tons) > 0
                        ? Number(row?.capacity_tons)
                        : undefined
                    }
                    value={row?.assigned_quantity}
                    onChange={e => updateRow(row?._key, 'assigned_quantity', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                  {Number(row?.capacity_tons) > 0 && (
                    <p className="text-xs" style={{ color: '#9ca3af' }}>
                      Max: {Number(row?.capacity_tons)?.toLocaleString('en-IN')} MT (truck capacity)
                    </p>
                  )}
                </div>

                {/* Rate per Ton */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>
                    Rate per Ton (₹) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 1200"
                    min="0"
                    value={row?.rate_per_ton}
                    onChange={e => updateRow(row?._key, 'rate_per_ton', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                  {Number(row?.rate_per_ton) > 0 && Number(row?.assigned_quantity) > 0 && (
                    <p className="text-xs font-medium" style={{ color: '#059669' }}>
                      Freight: ₹{(Number(row?.rate_per_ton) * Number(row?.assigned_quantity))?.toLocaleString('en-IN')}
                    </p>
                  )}
                </div>

                {/* Destination */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>
                    Delivery Destination
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Pune Warehouse"
                    value={row?.destination}
                    onChange={e => updateRow(row?._key, 'destination', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                </div>

                {/* Driver Name */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>Driver Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Ramesh Kumar"
                    value={row?.driver_name}
                    onChange={e => updateRow(row?._key, 'driver_name', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                </div>

                {/* Driver Phone */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>Driver Phone</label>
                  <input
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={row?.driver_phone}
                    onChange={e => updateRow(row?._key, 'driver_phone', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                </div>
                {/* DL Number */}
<div className="space-y-1">
  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>DL Number <span style={{ color: '#9ca3af' }}>(optional)</span></label>
  <input
    type="text"
    placeholder="e.g. MH0120190012345"
    value={row?.dl_number}
    onChange={e => updateRow(row?._key, 'dl_number', e?.target?.value?.toUpperCase())}
    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
  />
</div>
              </div>

              {/* Notes + Save Truck button */}
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-1 space-y-1">
                  <label className="block text-xs font-semibold" style={{ color: '#374151' }}>Notes (optional)</label>
                  <input
                    type="text"
                    placeholder="Any special instructions for this truck"
                    value={row?.notes}
                    onChange={e => updateRow(row?._key, 'notes', e?.target?.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-amber-300"
                    style={{ border: '1px solid #d1d5db', color: '#1f2937', background: '#fff' }}
                  />
                </div>
                <button
                  onClick={() => handleSaveTruck(row?._key)}
                  disabled={savingRowKey === row?._key || row?._saved}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                  style={{
                    background: row?._saved ? '#dcfce7' : savingRowKey === row?._key ? '#e5e7eb' : '#2563eb',
                    color: row?._saved ? '#166534' : savingRowKey === row?._key ? '#9ca3af' : '#ffffff',
                    cursor: row?._saved ? 'default' : 'pointer',
                    minWidth: '110px',
                  }}
                >
                  {savingRowKey === row?._key ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : row?._saved ? (
                    <>
                      <Icon name="CheckCircle" size={14} color="#166534" />
                      Saved
                    </>
                  ) : (
                    <>
                      <Icon name="Save" size={14} color="#fff" />
                      Save Truck
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}

          {/* Add truck button */}
          <button
            onClick={addRow}
            className="w-full py-2.5 rounded-xl text-sm font-semibold border-2 border-dashed transition-all hover:bg-amber-50 flex items-center justify-center gap-2"
            style={{ borderColor: '#fde68a', color: '#d97706' }}
          >
            <Icon name="Plus" size={16} color="#d97706" />
            Add Another Truck
          </button>
        </div>

        {/* Summary footer */}
        <div className="px-6 py-4 border-t" style={{ borderColor: '#f3f4f6', background: '#fafafa' }}>
          <div className="flex flex-wrap gap-4 text-xs mb-4">
            <div className="rounded-lg px-3 py-2" style={{ background: '#fef3c7' }}>
              <p style={{ color: '#9ca3af' }}>Total Trucks</p>
              <p className="font-bold text-sm" style={{ color: '#92400e' }}>{assignments?.length}</p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: '#dcfce7' }}>
              <p style={{ color: '#9ca3af' }}>Total Assigned</p>
              <p className="font-bold text-sm" style={{ color: '#166534' }}>{totalAssigned?.toLocaleString('en-IN')} MT</p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: remaining < 0 ? '#fee2e2' : '#f0fdf4' }}>
              <p style={{ color: '#9ca3af' }}>Remaining</p>
              <p className="font-bold text-sm" style={{ color: remaining < 0 ? '#dc2626' : '#374151' }}>
                {remaining?.toLocaleString('en-IN')} MT
              </p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: '#ede9fe' }}>
              <p style={{ color: '#9ca3af' }}>Total Freight</p>
              <p className="font-bold text-sm" style={{ color: '#7c3aed' }}>
                ₹{assignments?.reduce((s, r) => s + (Number(r?.rate_per_ton || 0) * Number(r?.assigned_quantity || 0)), 0)?.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all"
              style={{ border: '1px solid #d1d5db', color: '#374151' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || assignments?.length === 0}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-2"
              style={{ background: '#059669', color: '#ffffff' }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Icon name="Check" size={15} />
                  Save All Truck Assignments
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        show={errorModal?.show}
        message={errorModal?.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default TruckAssignmentModal;



